import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '../cn'

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close

export function DialogContent({
  className,
  children,
  title,
  description,
  width = 'md',
}: {
  className?: string
  children: React.ReactNode
  title: string
  description?: string
  width?: 'sm' | 'md' | 'lg' | 'xl'
}) {
  const widths = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-3xl', xl: 'sm:max-w-5xl' }
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[var(--c-overlay)] data-[state=open]:animate-in data-[state=open]:fade-in" />
      <DialogPrimitive.Content
        // A dialog is rendered through `DialogPrimitive.Portal`, elsewhere in
        // the real DOM — but React dispatches its synthetic events through
        // the REACT tree, not the real DOM one, so a drag/drop anywhere
        // inside this dialog still bubbles, in React's eyes, straight past
        // it to whatever page rendered the trigger. A page that itself
        // listens for drops (the ledger's own "drop a receipt anywhere to
        // start a transaction") would then react to a drop this dialog
        // already handled itself — an extra upload, a second dialog opening
        // on top of this one, from one user action. Stopping the whole
        // drag/drop family here, once, closes that off for every dialog
        // instead of requiring each one that happens to accept a drop to
        // remember it — the page underneath should not have to know what
        // might be open above it either way.
        onDragEnter={(event) => event.stopPropagation()}
        onDragOver={(event) => event.stopPropagation()}
        onDragLeave={(event) => event.stopPropagation()}
        onDrop={(event) => event.stopPropagation()}
        className={cn(
          // Phone: the dialog *is* the screen. A form with eight fields in a
          // centred card on a 320 px viewport is unusable.
          'fixed inset-0 z-50 flex flex-col bg-surface',
          // Tablet up: a centred card again.
          'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:w-[calc(100vw-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2',
          'sm:rounded-[var(--radius-panel)] sm:border sm:border-rule sm:shadow-(--shadow-dialog)',
          'focus:outline-none',
          widths[width],
          className,
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-rule px-4 py-3">
          <div className="min-w-0">
            <DialogPrimitive.Title className="text-lg font-semibold text-ink">{title}</DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-0.5 text-xs text-ink-muted">
                {description}
              </DialogPrimitive.Description>
            ) : null}
          </div>
          <DialogPrimitive.Close
            aria-label="Schließen"
            className="-mr-1 rounded-[var(--radius-control)] p-1 text-ink-faint hover:bg-sunken hover:text-ink"
          >
            <X className="size-4" />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export function DialogBody({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto p-4 sm:max-h-[70vh] sm:flex-none', className)} {...props} />
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex shrink-0 items-center justify-end gap-3 border-t border-rule px-5 py-4', className)}
      {...props}
    />
  )
}
